import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import {
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { TabProps, RechartsClickData } from './types'
import { useFormatters } from '../../contexts/PreferencesContext'
import { ChartWrapper } from './ChartWrapper'
import api from '../../lib/api'
import { formatCompactNumber } from '../../lib/utils'
import { useDrilldownAction } from '../drilldown'

// Transport mode icons and colors
const TRANSPORT_CONFIG = {
  air: { color: '#3b82f6', icon: '✈️', label: 'Air' },
  sea: { color: '#06b6d4', icon: '🚢', label: 'Sea' },
  rail: { color: '#8b5cf6', icon: '🚂', label: 'Rail' },
  road: { color: '#10b981', icon: '🚛', label: 'Road' },
}

// Customer count selector component
function TopNSelector({
  value,
  onChange,
}: {
  value: number
  onChange: (value: number) => void
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="text-xs border rounded px-2 py-1 bg-background"
    >
      <option value={5}>Top 5</option>
      <option value={10}>Top 10</option>
      <option value={15}>Top 15</option>
    </select>
  )
}

// Transport breakdown card component
function TransportCard({
  mode,
  emissions,
  distance,
  percentage,
  total: _total,
}: {
  mode: keyof typeof TRANSPORT_CONFIG
  emissions: number
  distance: number
  percentage: number
  total: number
}) {
  const config = TRANSPORT_CONFIG[mode]

  return (
    <Card className="shadow-sm">
      <CardContent className="pt-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-2xl">{config.icon}</span>
            <span className="font-medium">{config.label}</span>
          </div>
          <div
            className="text-sm font-bold px-2 py-1 rounded"
            style={{ backgroundColor: config.color, color: 'white' }}
          >
            {percentage.toFixed(1)}%
          </div>
        </div>
        <div className="space-y-1 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Total Emission</span>
            <span className="font-semibold">{emissions.toLocaleString('en-GB', { maximumFractionDigits: 2 })} KG CO₂e</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Distance</span>
            <span className="font-semibold">{distance.toLocaleString('en-GB', { maximumFractionDigits: 0 })} KM</span>
          </div>
        </div>
        {/* Mini progress bar */}
        <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${percentage}%`, backgroundColor: config.color }}
          />
        </div>
      </CardContent>
    </Card>
  )
}

interface TransportBreakdownItem {
  mode: string
  emissions: number
  distance: number
  percentage: number
  [key: string]: unknown
}

interface CustomerEmissionItem {
  customer: string
  fullName: string
  emissions: number
  percentage: number
  [key: string]: unknown
}

interface EmissionsDisplayData {
  total: number
  byTransport: TransportBreakdownItem[]
  byCustomer: CustomerEmissionItem[]
  byMonth: Array<{ month: string; air: number; sea: number; rail: number; road: number; total: number }>
  trend: Array<{ month: string; emissions: number; trend_line: number }>
}

export function EmissionsTab({ data: _data, filters, dataPeriod: _dataPeriod, orderType: _orderType }: TabProps) {
  const { formatNumber } = useFormatters()
  const [topCustomerCount, setTopCustomerCount] = useState(5)
  const [emissionsData, setEmissionsData] = useState<EmissionsDisplayData | null>(null)
  const [loading, setLoading] = useState(true)

  // Hook for programmatic drill-down (for chart clicks)
  const { openDrilldown } = useDrilldownAction()

  // Convert dashboard filters to drilldown API format
  const drilldownFilters = {
    ...(filters?.dateFrom && { date_from: filters.dateFrom }),
    ...(filters?.dateTo && { date_to: filters.dateTo }),
    ...(filters?.customer && { customer: filters.customer }),
    ...(filters?.supplier && { supplier: filters.supplier }),
    ...(filters?.site && { site: filters.site }),
    ...(filters?.siteCountry && { site_country: filters.siteCountry }),
    ...(filters?.customerGroup && { customer_group: filters.customerGroup }),
    ...(filters?.customerCountry && { customer_country: filters.customerCountry }),
  }

  // Helper function to parse month string to date range
  const parseMonthToDateRange = (monthStr: string): { date_from: string; date_to: string } | null => {
    try {
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      const parts = monthStr.trim().split(' ')
      const monthIndex = monthNames.indexOf(parts[0])
      if (monthIndex === -1) return null
      let year: number
      if (parts.length >= 2 && parts[1]) {
        year = parseInt(parts[1])
        if (year < 100) year += 2000
      } else {
        year = new Date().getFullYear()
      }
      // CR-4671: Format in local timezone — see dashboardUtils.ts for context.
      const startDate = new Date(year, monthIndex, 1)
      const endDate = new Date(year, monthIndex + 1, 0)
      const fmt = (d: Date): string =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      return {
        date_from: fmt(startDate),
        date_to: fmt(endDate),
      }
    } catch {
      return null
    }
  }

  // Handle chart click - open ASN list for the selected period
  const handleChartClick = (chartData: RechartsClickData | null) => {
    if (!chartData) return
    let month: string | null = null
    if (chartData.activePayload && chartData.activePayload[0]) {
      month = (chartData.activePayload[0].payload?.['month'] as string) || null
    } else if (chartData.activeLabel) {
      month = chartData.activeLabel
    }
    if (!month) return

    // Parse month to date range for API filtering
    const dateRange = parseMonthToDateRange(month)
    const chartFilters = dateRange ? {
      ...drilldownFilters,
      date_from: dateRange.date_from,
      date_to: dateRange.date_to,
    } : drilldownFilters

    // Open spend drilldown as emissions are related to ASNs/shipments
    openDrilldown('spend', chartFilters, month)
  }

  // Fetch emissions data from ASNs
  useEffect(() => {
    const fetchEmissionsData = async () => {
      try {
        setLoading(true)
        const response = await api.get('/purchasing/asns/', {
          params: {
            page_size: 1000,
            ...(filters?.dateFrom && { shipment_date_after: filters.dateFrom }),
            ...(filters?.dateTo && { shipment_date_before: filters.dateTo }),
            ...(filters?.customer && { customer: filters.customer }),
          }
        })
        const asns = response.data.results || response.data || []

        // Process ASN data to calculate emissions by transport
        const byTransport: Record<string, { emissions: number; distance: number; count: number }> = {
          air: { emissions: 0, distance: 0, count: 0 },
          sea: { emissions: 0, distance: 0, count: 0 },
          rail: { emissions: 0, distance: 0, count: 0 },
          road: { emissions: 0, distance: 0, count: 0 },
        }

        // Track by customer
        const byCustomer: Record<string, number> = {}

        // Track by month
        const byMonth: Record<string, Record<string, number>> = {}

        asns.forEach((asn: Record<string, unknown>) => {
          const transport = ((asn['method_of_transport'] as string) || 'road').toLowerCase()
          const emissions = parseFloat(String(asn['carbon_emission_kg'] || 0)) || parseFloat(String(asn['calculated_carbon_emission'] || 0)) || 0
          const distance = parseFloat(String(asn['distance_km'] || 0)) || 0

          if (byTransport[transport]) {
            byTransport[transport].emissions += emissions
            byTransport[transport].distance += distance
            byTransport[transport].count += 1
          }

          // Aggregate by customer
          const customerObj = asn['customer'] as Record<string, unknown> | undefined
          const customerName = (asn['customer_name'] as string) || (customerObj?.['name'] as string) || 'Unknown'
          byCustomer[customerName] = (byCustomer[customerName] || 0) + emissions

          // Aggregate by month
          if (asn['shipment_date']) {
            const _d = new Date(asn['shipment_date'] as string)
            const _months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
            const monthKey = `${_months[_d.getMonth()]} ${String(_d.getFullYear()).slice(2)}`
            if (!byMonth[monthKey]) {
              byMonth[monthKey] = { air: 0, sea: 0, rail: 0, road: 0 }
            }
            if (byMonth[monthKey][transport] !== undefined) {
              byMonth[monthKey][transport] += emissions
            }
          }
        })

        const totalEmissions = Object.values(byTransport).reduce((sum, t) => sum + t.emissions, 0)

        // Convert to array format
        const transportData = Object.entries(byTransport).map(([mode, data]) => ({
          mode,
          emissions: data.emissions,
          distance: data.distance,
          percentage: totalEmissions > 0 ? (data.emissions / totalEmissions) * 100 : 0,
        }))

        // Sort customers by emissions and take top N
        const customerData = Object.entries(byCustomer)
          .sort((a, b) => b[1] - a[1])
          .map(([name, emissions]) => ({
            customer: name.length > 15 ? name.substring(0, 15) + '...' : name,
            fullName: name,
            emissions,
            percentage: totalEmissions > 0 ? (emissions / totalEmissions) * 100 : 0,
          }))

        // Convert month data to array
        const monthlyData = Object.entries(byMonth)
          .sort((a, b) => {
            const dateA = new Date(a[0])
            const dateB = new Date(b[0])
            return dateA.getTime() - dateB.getTime()
          })
          .slice(-6)
          .map(([month, mData]) => ({
            month,
            air: mData['air'] || 0,
            sea: mData['sea'] || 0,
            rail: mData['rail'] || 0,
            road: mData['road'] || 0,
            total: (mData['air'] || 0) + (mData['sea'] || 0) + (mData['rail'] || 0) + (mData['road'] || 0),
          }))

        // Generate trend data
        const trendData = monthlyData.map((m, i, arr) => {
          // Simple moving average for trend line
          const windowSize = Math.min(3, i + 1)
          const windowStart = Math.max(0, i - windowSize + 1)
          const windowSum = arr.slice(windowStart, i + 1).reduce((sum, item) => sum + item.total, 0)
          return {
            month: m.month,
            emissions: m.total,
            trend_line: windowSum / windowSize,
          }
        })

        setEmissionsData({
          total: totalEmissions,
          byTransport: transportData,
          byCustomer: customerData,
          byMonth: monthlyData,
          trend: trendData,
        })
      } catch (error) {
        console.error('Error fetching emissions data:', error)
        // Show empty state on error - no fake data on live platform
        setEmissionsData({
          total: 0,
          byTransport: [],
          byCustomer: [],
          byMonth: [],
          trend: [],
        })
      } finally {
        setLoading(false)
      }
    }

    fetchEmissionsData()
  }, [filters?.dateFrom, filters?.dateTo, filters?.customer])

  const emptyData: EmissionsDisplayData = {
    total: 0,
    byTransport: [],
    byCustomer: [],
    byMonth: [],
    trend: [],
  }

  const displayData = emissionsData || emptyData
  const transportBreakdown = displayData.byTransport || []
  const topCustomers = (displayData.byCustomer || []).slice(0, topCustomerCount)

  // Pie chart colors
  const PIE_COLORS = ['#3b82f6', '#06b6d4', '#8b5cf6', '#10b981']

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-card rounded-lg shadow-sm p-4">
        <h2 className="text-lg font-semibold">CO₂ Emissions Overview</h2>
        <p className="text-sm text-muted-foreground">
          Carbon emissions calculated from ASN shipment data
        </p>
      </div>

      {/* Total Carbon Emissions Breakdown */}
      <Card className="shadow-sm bg-gradient-to-r from-green-50 to-blue-50 border-green-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Total Carbon Emissions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="text-3xl font-bold text-green-700">
              {formatNumber(displayData.total)} KG CO₂e
            </div>
            <div className="grid grid-cols-4 gap-4 text-sm">
              {transportBreakdown.map((t: TransportBreakdownItem) => (
                <div key={t.mode} className="text-center">
                  <div className="font-medium" style={{ color: TRANSPORT_CONFIG[t.mode as keyof typeof TRANSPORT_CONFIG]?.color }}>
                    {TRANSPORT_CONFIG[t.mode as keyof typeof TRANSPORT_CONFIG]?.label}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {t.emissions.toLocaleString('en-GB', { maximumFractionDigits: 2 })} KG
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Row - Pie Chart and Transport Cards */}
      <div className="grid grid-cols-3 gap-4">
        {/* Main Pie Chart */}
        <Card className="shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Emissions by Transport Mode</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={transportBreakdown}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={90}
                  dataKey="emissions"
                  nameKey="mode"
                  label={(props: Record<string, unknown>) => `${TRANSPORT_CONFIG[props['mode'] as keyof typeof TRANSPORT_CONFIG]?.label}: ${(props['percentage'] as number)?.toFixed(1)}%`}
                  labelLine={{ stroke: '#888', strokeWidth: 1 }}
                >
                  {transportBreakdown.map((entry: TransportBreakdownItem, index: number) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={TRANSPORT_CONFIG[entry.mode as keyof typeof TRANSPORT_CONFIG]?.color || PIE_COLORS[index]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number, name: string) => [
                    `${value.toLocaleString('en-GB', { maximumFractionDigits: 2 })} KG CO₂e`,
                    TRANSPORT_CONFIG[name as keyof typeof TRANSPORT_CONFIG]?.label || name
                  ]}
                />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Transport Cards Grid */}
        <div className="col-span-2 grid grid-cols-2 gap-4">
          {transportBreakdown.map((t: TransportBreakdownItem) => (
            <TransportCard
              key={t.mode}
              mode={t.mode as keyof typeof TRANSPORT_CONFIG}
              emissions={t.emissions}
              distance={t.distance}
              percentage={t.percentage}
              total={displayData.total}
            />
          ))}
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-2 gap-4">
        {/* Impact Over Time */}
        <ChartWrapper
          title="Impact Over Time"
          data={displayData.trend || []}
          exportFilename="emissions-impact-over-time"
          legend={[
            { color: '#10b981', label: 'Aggregation' },
            { color: '#ef4444', label: 'Trend Line' },
          ]}
        >
          <ResponsiveContainer width="100%" height={250}>
            <LineChart
              data={displayData.trend || []}
              onClick={handleChartClick}
              style={{ cursor: 'pointer' }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={formatCompactNumber} />
              <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} formatter={(value: number) => [`${value.toLocaleString('en-GB', { maximumFractionDigits: 2 })} KG CO₂e`]} />
              <Area
                type="monotone"
                dataKey="emissions"
                stroke="#10b981"
                fill="#bbf7d0"
                name="Aggregation"
              />
              <Line
                type="monotone"
                dataKey="trend_line"
                stroke="#ef4444"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
                name="Trend Line"
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartWrapper>

        {/* Top CO₂e Contributing Customers */}
        <ChartWrapper
          title="Top CO₂e Contributing Customers"
          data={topCustomers}
          exportFilename="emissions-by-customer"
          headerActions={
            <TopNSelector value={topCustomerCount} onChange={setTopCustomerCount} />
          }
        >
          <ResponsiveContainer width="100%" height={250}>
            <PieChart
              onClick={() => openDrilldown('spend', drilldownFilters)}
              style={{ cursor: 'pointer' }}
            >
              <Pie
                data={topCustomers}
                cx="50%"
                cy="50%"
                innerRadius={40}
                outerRadius={80}
                dataKey="emissions"
                nameKey="customer"
                label={(props: Record<string, unknown>) => `${props['customer']}: ${(props['percentage'] as number)?.toFixed(1)}%`}
              >
                {topCustomers.map((_: CustomerEmissionItem, index: number) => (
                  <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: number, name: string, props: { payload?: Record<string, unknown> }) => [
                  `${value.toLocaleString('en-GB', { maximumFractionDigits: 2 })} KG CO₂e`,
                  (props.payload?.['fullName'] as string) || name
                ]}
              />
            </PieChart>
          </ResponsiveContainer>
        </ChartWrapper>
      </div>

      {/* Carbon Emissions by Method of Transport (Stacked Bar) */}
      <ChartWrapper
        title="Carbon Emissions by Method of Transport"
        data={displayData.byMonth || []}
        exportFilename="emissions-by-transport-monthly"
        legend={[
          { color: TRANSPORT_CONFIG.air.color, label: 'Air' },
          { color: TRANSPORT_CONFIG.sea.color, label: 'Sea' },
          { color: TRANSPORT_CONFIG.rail.color, label: 'Rail' },
          { color: TRANSPORT_CONFIG.road.color, label: 'Road' },
        ]}
      >
        <ResponsiveContainer width="100%" height={280}>
          <BarChart
            data={displayData.byMonth || []}
            onClick={handleChartClick}
            style={{ cursor: 'pointer' }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis dataKey="month" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={formatCompactNumber} />
            <Tooltip
              cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }}
              formatter={(value: number, name: string) => [
                `${value.toLocaleString('en-GB', { maximumFractionDigits: 2 })} KG CO₂e`,
                TRANSPORT_CONFIG[name as keyof typeof TRANSPORT_CONFIG]?.label || name
              ]}
            />
            <Bar dataKey="air" stackId="a" fill={TRANSPORT_CONFIG.air.color} name="air" />
            <Bar dataKey="sea" stackId="a" fill={TRANSPORT_CONFIG.sea.color} name="sea" />
            <Bar dataKey="rail" stackId="a" fill={TRANSPORT_CONFIG.rail.color} name="rail" />
            <Bar dataKey="road" stackId="a" fill={TRANSPORT_CONFIG.road.color} name="road" />
          </BarChart>
        </ResponsiveContainer>
      </ChartWrapper>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> Emissions data is calculated based on ASN shipment information using standard emission factors:
          Road (0.213), Rail (0.022), Air (0.602), Sea (0.016) kg CO₂ per tonne-km.
          {loading && ' Loading latest data...'}
        </p>
      </div>
    </div>
  )
}
